import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, List, ListItem, ListItemAvatar,
    Avatar, ListItemText, Divider, Chip, TextField, MenuItem, Tabs, Tab,
    Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    InputAdornment
} from '@mui/material';
import {
    Cake as CakeIcon, Search as SearchIcon, Sms as SmsIcon,
    CalendarMonth as CalendarIcon, Download as DownloadIcon,
    Celebration as CelebrationIcon
} from '@mui/icons-material';
import { studentAPI } from '../services/api';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const getAge = (dobString) => {
    if (!dobString) return 0;
    const today = new Date();
    const dob = new Date(dobString);
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
    }
    return age;
};

const getDaysAway = (dobString) => {
    if (!dobString) return 0;
    const today = new Date();
    const dob = new Date(dobString);
    const bdayThisYear = new Date(today.getFullYear(), dob.getMonth(), dob.getDate());
    if (bdayThisYear < today && (today.getTime() - bdayThisYear.getTime()) > 0) {
        bdayThisYear.setFullYear(today.getFullYear() + 1);
    }
    const diffTime = bdayThisYear.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const BirthdayDashboard = () => {
    const [stats, setStats] = useState({ today: [], upcoming: [], monthlyDistribution: Array(12).fill(0), monthCount: 0, weekCount: 0 });
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(0);
    const [monthFilter, setMonthFilter] = useState(new Date().getMonth());
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => { fetchBirthdays(); }, []);

    const fetchBirthdays = async () => {
        try {
            const res = await studentAPI.getBirthdayStats();
            setStats(res.data.data || { today: [], upcoming: [], monthlyDistribution: Array(12).fill(0), monthCount: 0, weekCount: 0 });
        } catch (error) {
            console.error("Failed to fetch birthdays");
        } finally {
            setLoading(false);
        }
    };

    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthlyData = stats.monthlyDistribution || Array(12).fill(0);

    const todayBirthdays = (stats.today || []).map(s => ({
        name: `${s.user?.firstName} ${s.user?.lastName}`,
        class: s.class ? `${s.class.name} - ${s.section?.name || ''}` : '-',
        dob: s.user?.dateOfBirth ? new Date(s.user.dateOfBirth).toLocaleDateString() : '-',
        age: getAge(s.user?.dateOfBirth),
        gender: s.user?.gender === 'female' ? 'F' : 'M',
        phone: s.user?.phone || '-'
    }));

    const upcomingBirthdays = (stats.upcoming || []).map(s => ({
        name: `${s.user?.firstName} ${s.user?.lastName}`,
        class: s.class ? `${s.class.name} - ${s.section?.name || ''}` : '-',
        dob: s.user?.dateOfBirth ? new Date(s.user.dateOfBirth).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-',
        age: getAge(s.user?.dateOfBirth),
        daysAway: getDaysAway(s.user?.dateOfBirth)
    }));

    const handleExport = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Birthdays');

        worksheet.columns = [
            { header: 'Student Name', key: 'name', width: 25 },
            { header: 'Class', key: 'class', width: 15 },
            { header: 'Gender', key: 'gender', width: 10 },
            { header: 'Age', key: 'age', width: 10 },
            { header: 'Contact No', key: 'phone', width: 15 },
            { header: 'Date of Birth', key: 'dob', width: 15 }
        ];

        // Combine today and upcoming birthdays for export
        const allBirthdays = [
            ...todayBirthdays.map(s => ({ ...s, status: 'Today' })),
            ...upcomingBirthdays.map(s => ({ ...s, status: 'Upcoming' }))
        ];

        if (allBirthdays.length === 0) {
            toast.warning('No birthday data to export');
            return;
        }

        allBirthdays.forEach(row => {
            worksheet.addRow(row);
        });

        // Style header row
        const headerRow = worksheet.getRow(1);
        headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        headerRow.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFC2185B' }
        };

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `Birthday_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const chartData = {
        labels: months.map(m => m.substring(0, 3)),
        datasets: [{
            label: 'Birthdays',
            data: monthlyData,
            backgroundColor: monthlyData.map((_, i) => i === new Date().getMonth() ? '#1a9e8f' : '#e0e0e0'),
            borderRadius: 4, barThickness: 24,
        }],
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CakeIcon sx={{ color: '#e91e63', fontSize: 28 }} />
                    <Typography variant="h5" fontWeight={700}>Birthday Dashboard</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" startIcon={<SmsIcon />} size="small" onClick={() => toast.success('Wishes queued to be sent!')}>Send Wishes</Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />} size="small" onClick={handleExport}>Export</Button>
                </Box>
            </Box>

            {/* Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ background: 'linear-gradient(135deg, #e91e63, #f48fb1)' }}>
                        <CardContent sx={{ textAlign: 'center', color: '#fff' }}>
                            <CakeIcon sx={{ fontSize: 28, mb: 0.5 }} />
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>Today's Birthdays</Typography>
                            <Typography variant="h3" fontWeight={700}>{todayBirthdays.length}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">This Week</Typography>
                        <Typography variant="h4" fontWeight={700}>{stats.weekCount || 0}</Typography>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">This Month</Typography>
                        <Typography variant="h4" fontWeight={700}>{stats.monthCount || 0}</Typography>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Total (Year)</Typography>
                        <Typography variant="h4" fontWeight={700}>{stats.monthlyDistribution ? stats.monthlyDistribution.reduce((a, b) => a + b, 0) : 0}</Typography>
                    </CardContent></Card>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Today's list */}
                <Grid item xs={12} md={5}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <CelebrationIcon sx={{ color: '#e91e63' }} />
                                <Typography variant="subtitle2" fontWeight={700}>Today's Birthdays 🎂</Typography>
                            </Box>
                            <List dense disablePadding>
                                {todayBirthdays.map((s, i) => (
                                    <React.Fragment key={i}>
                                        <ListItem sx={{ px: 0 }}>
                                            <ListItemAvatar sx={{ minWidth: 40 }}>
                                                <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: s.gender === 'M' ? '#2196f3' : '#e91e63' }}>{s.name[0]}</Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={<Typography variant="body2" fontWeight={600}>{s.name}</Typography>}
                                                secondary={`${s.class} • Age ${s.age} • ${s.dob}`}
                                            />
                                            <Chip label="🎂" size="small" sx={{ bgcolor: '#fce4ec' }} />
                                        </ListItem>
                                        {i < todayBirthdays.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Chart */}
                <Grid item xs={12} md={7}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Monthly Distribution</Typography>
                            <Box height={220}>
                                <Bar data={chartData} options={{
                                    responsive: true, maintainAspectRatio: false,
                                    plugins: { legend: { display: false } },
                                    scales: { y: { beginAtZero: true, grid: { color: '#f0f0f0' } }, x: { grid: { display: false } } }
                                }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Upcoming */}
            <Card>
                <CardContent sx={{ p: '12px !important' }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ px: 1, pb: 1 }}>Upcoming Birthdays (Next 7 Days)</Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Student</TableCell>
                                    <TableCell>Class</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Age</TableCell>
                                    <TableCell>Days Away</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {upcomingBirthdays.map((s, i) => (
                                    <TableRow key={i} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                                        <TableCell>{s.class}</TableCell>
                                        <TableCell>{s.dob}</TableCell>
                                        <TableCell>{s.age}</TableCell>
                                        <TableCell><Chip size="small" label={`In ${s.daysAway} day${s.daysAway > 1 ? 's' : ''}`} color={s.daysAway === 1 ? 'warning' : 'default'} /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );
};

export default BirthdayDashboard;
